const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MINUTES = 60;
const DAY_MINUTES = 24 * HOUR_MINUTES;
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export type TimelineDateValue = string | Date | null | undefined;

export interface TimelineDateRange {
  from: Date | null;
  to: Date | null;
}

export interface TimelineDateShape {
  startDate: Date | null;
  dueDate: Date | null;
  visualStart: Date | null;
  visualEnd: Date | null;
  kind: "range" | "start-only" | "due-only" | "undated";
}

export interface TimelineDatedItem {
  startDate?: TimelineDateValue;
  dueDate?: TimelineDateValue;
}

export interface TimelineDurationParts {
  days: number;
  hours: number;
  minutes: number;
  totalMinutes: number;
}

export interface TimelineDurationSummary {
  total: TimelineDurationParts;
  working: TimelineDurationParts;
}

export const startOfLocalDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const addDays = (date: Date, days: number): Date => {
  const next = startOfLocalDay(date);
  next.setDate(next.getDate() + days);
  return next;
};

export const diffInCalendarDays = (from: Date, to: Date): number =>
  Math.round(
    (startOfLocalDay(to).getTime() - startOfLocalDay(from).getTime()) / DAY_MS,
  );

export const parseTimelineDate = (value: TimelineDateValue): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : startOfLocalDay(value);
  }

  const stringValue = String(value);
  const dateOnlyMatch = stringValue.match(DATE_ONLY_PATTERN);

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch.map(Number);

    return new Date(year, month - 1, day);
  }

  const dateTime = new Date(stringValue);

  if (!Number.isNaN(dateTime.getTime())) {
    return startOfLocalDay(dateTime);
  }

  const datePart = stringValue.slice(0, 10);
  const [year, month, day] = datePart.split("-").map(Number);

  if (!year || !month || !day) {
    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? null : startOfLocalDay(fallback);
  }

  return new Date(year, month - 1, day);
};

export const parseTimelineDateTime = (
  value: TimelineDateValue,
): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  }

  const stringValue = String(value);
  const dateOnlyMatch = stringValue.match(DATE_ONLY_PATTERN);

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch.map(Number);

    return new Date(year, month - 1, day);
  }

  const dateTime = new Date(stringValue);

  if (!Number.isNaN(dateTime.getTime())) {
    return dateTime;
  }

  const datePart = stringValue.slice(0, 10);
  const [year, month, day] = datePart.split("-").map(Number);

  if (!year || !month || !day) {
    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  return new Date(year, month - 1, day);
};

export const formatTimelineDate = (
  value: TimelineDateValue,
  locale = "it-IT",
): string => {
  const date = parseTimelineDate(value);
  if (!date) {
    return "-";
  }

  return date.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const formatTimelineDateTime = (
  value: TimelineDateValue,
  locale = "it-IT",
): string => {
  const date = parseTimelineDateTime(value);
  if (!date) {
    return "-";
  }

  const dateLabel = date.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timeLabel = `${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes(),
  ).padStart(2, "0")}`;

  return `${dateLabel} ${timeLabel}`;
};

export const formatTimelineInputDate = (value: TimelineDateValue): string => {
  const date = parseTimelineDate(value);
  if (!date) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const isSameTimelineDay = (
  left: TimelineDateValue,
  right: TimelineDateValue,
): boolean => {
  const leftDate = parseTimelineDate(left);
  const rightDate = parseTimelineDate(right);

  if (!leftDate || !rightDate) {
    return leftDate === rightDate;
  }

  return leftDate.getTime() === rightDate.getTime();
};

export const isSameTimelineDateTime = (
  left: TimelineDateValue,
  right: TimelineDateValue,
): boolean => {
  const leftDate = parseTimelineDateTime(left);
  const rightDate = parseTimelineDateTime(right);

  if (!leftDate || !rightDate) {
    return leftDate === rightDate;
  }

  return leftDate.getTime() === rightDate.getTime();
};

export const getInclusiveDurationDays = (
  start: TimelineDateValue,
  end: TimelineDateValue,
): number | null => {
  const startDate = parseTimelineDate(start);
  const endDate = parseTimelineDate(end);

  if (!startDate || !endDate) {
    return null;
  }

  return Math.max(1, diffInCalendarDays(startDate, endDate) + 1);
};

export const getWorkingDaysInclusive = (
  start: TimelineDateValue,
  end: TimelineDateValue,
): number | null => {
  const startDate = parseTimelineDate(start);
  const endDate = parseTimelineDate(end);

  if (!startDate || !endDate) {
    return null;
  }

  const totalDays = getInclusiveDurationDays(startDate, endDate) ?? 0;
  let workingDays = 0;

  for (let index = 0; index < totalDays; index += 1) {
    const date = addDays(startDate, index);
    const weekday = date.getDay();
    if (weekday !== 0 && weekday !== 6) {
      workingDays += 1;
    }
  }

  return workingDays;
};

const isWorkingDay = (date: Date): boolean => {
  const weekday = date.getDay();
  return weekday !== 0 && weekday !== 6;
};

const toDurationParts = (totalMinutes: number): TimelineDurationParts => {
  const safeMinutes = Math.max(0, Math.round(totalMinutes));
  const days = Math.floor(safeMinutes / DAY_MINUTES);
  const remainingMinutes = safeMinutes - days * DAY_MINUTES;
  const hours = Math.floor(remainingMinutes / HOUR_MINUTES);
  const minutes = remainingMinutes - hours * HOUR_MINUTES;

  return {
    days,
    hours,
    minutes,
    totalMinutes: safeMinutes,
  };
};

const getWorkingDurationMinutes = (startDate: Date, endDate: Date): number => {
  let cursor = new Date(startDate.getTime());
  let minutes = 0;

  while (cursor < endDate) {
    const nextDay = startOfLocalDay(cursor);
    nextDay.setDate(nextDay.getDate() + 1);

    const segmentEnd =
      nextDay.getTime() < endDate.getTime()
        ? nextDay
        : new Date(endDate.getTime());

    if (isWorkingDay(cursor)) {
      minutes += (segmentEnd.getTime() - cursor.getTime()) / (60 * 1000);
    }

    cursor = segmentEnd;
  }

  return minutes;
};

export const getTimelineDurationSummary = (
  start: TimelineDateValue,
  end: TimelineDateValue,
): TimelineDurationSummary | null => {
  const startDate = parseTimelineDateTime(start);
  const endDate = parseTimelineDateTime(end);

  if (!startDate || !endDate || endDate < startDate) {
    return null;
  }

  const totalMinutes = (endDate.getTime() - startDate.getTime()) / (60 * 1000);

  return {
    total: toDurationParts(totalMinutes),
    working: toDurationParts(getWorkingDurationMinutes(startDate, endDate)),
  };
};

export const formatTimelineDurationParts = (
  parts: TimelineDurationParts,
): string => {
  const labels: string[] = [];

  if (parts.days > 0) {
    labels.push(`${parts.days} g`);
  }

  if (parts.hours > 0) {
    labels.push(`${parts.hours} h`);
  }

  if (parts.minutes > 0) {
    labels.push(`${parts.minutes} min`);
  }

  return labels.join(" ") || "0 min";
};

export const formatTimelineDurationSummary = (
  summary: TimelineDurationSummary | null,
): string | null => {
  if (!summary) {
    return null;
  }

  return `${formatTimelineDurationParts(
    summary.total,
  )} / ${formatTimelineDurationParts(summary.working)} lav.`;
};

export const getTimelineDurationLabel = (
  start: TimelineDateValue,
  end: TimelineDateValue,
): string | null =>
  formatTimelineDurationSummary(getTimelineDurationSummary(start, end));

export const getTimelineDateShape = (
  item: TimelineDatedItem,
): TimelineDateShape => {
  const startDate = parseTimelineDate(item.startDate);
  const dueDate = parseTimelineDate(item.dueDate);

  if (startDate && dueDate) {
    const visualStart = startDate <= dueDate ? startDate : dueDate;
    const visualEnd = startDate <= dueDate ? dueDate : startDate;

    return {
      startDate,
      dueDate,
      visualStart,
      visualEnd,
      kind: "range",
    };
  }

  if (startDate) {
    return {
      startDate,
      dueDate: null,
      visualStart: startDate,
      visualEnd: startDate,
      kind: "start-only",
    };
  }

  if (dueDate) {
    return {
      startDate: null,
      dueDate,
      visualStart: dueDate,
      visualEnd: dueDate,
      kind: "due-only",
    };
  }

  return {
    startDate: null,
    dueDate: null,
    visualStart: null,
    visualEnd: null,
    kind: "undated",
  };
};

export const intersectsRange = (
  item: TimelineDatedItem,
  range: TimelineDateRange,
): boolean => {
  const shape = getTimelineDateShape(item);

  if (!shape.visualStart || !shape.visualEnd) {
    return false;
  }

  const from = range.from ? startOfLocalDay(range.from) : null;
  const to = range.to ? startOfLocalDay(range.to) : null;

  if (from && shape.visualEnd < from) {
    return false;
  }

  if (to && shape.visualStart > to) {
    return false;
  }

  return true;
};
